import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/client.js'
import type { WorkerTask } from '../api/types.js'
import { t } from '../i18n/index.js'
import { formatDateLong, todayIso } from '../lib/format.js'
import { pendingTaskIds, subscribeOutbox, sync, type OutboxState } from '../lib/outbox.js'
import { WorkerNav } from './nav.js'

export function WorkerShell({
  title,
  subtitle,
  back,
  action,
  hideNav,
  children,
}: {
  title: string
  subtitle?: string
  back?: string
  action?: ReactNode
  hideNav?: boolean
  children: ReactNode
}) {
  const { openToday, openUpcoming } = useWorkerTasks()

  return (
    <div className="wk" style={hideNav ? { paddingBottom: 24 } : undefined}>
      <header className="wk-top">
        <div>
          {back && (
            <Link to={back} style={{ display: 'block', marginBottom: 2 }}>
              ← {t('app.back')}
            </Link>
          )}
          <h1>{title}</h1>
          <div className="date">{subtitle ?? (back ? undefined : formatDateLong(todayIso()))}</div>
        </div>
        {action}
      </header>
      <SyncBar />
      <div className="wk-body">{children}</div>
      {!hideNav && <WorkerNav openToday={openToday} openUpcoming={openUpcoming} />}
    </div>
  )
}

/**
 * The one piece of status a worker needs: whether their taps have actually
 * reached the office. Without it, "did it save?" becomes a phone call.
 */
export function SyncBar() {
  const [state, setState] = useState<OutboxState>({ pending: 0, syncing: false, online: navigator.onLine })

  useEffect(() => subscribeOutbox(setState), [])

  if (state.pending === 0 && state.online) return null

  const className = !state.online ? 'offline' : state.pending > 0 ? 'pending' : 'synced'
  const message = !state.online
    ? t('sync.offline')
    : state.syncing
      ? t('sync.syncing')
      : state.pending === 1
        ? t('sync.pending_one')
        : t('sync.pending_many', { count: state.pending })

  return (
    <div className={`wk-sync ${className}`} role="status" aria-live="polite">
      <span aria-hidden="true">{state.online ? '↑' : '⚠'}</span>
      <span style={{ flex: 1 }}>{message}</span>
      {state.online && !state.syncing && state.pending > 0 && (
        <button type="button" className="btn ghost small" onClick={() => void sync()}>
          {t('common.retry')}
        </button>
      )}
    </div>
  )
}

export type OverlayTask = WorkerTask & { pendingUpload: boolean }

/**
 * Tasks from the server, overlaid with completions still sitting in the outbox.
 *
 * Without this overlay a worker who taps "done" with no signal would see the
 * task pop back to open on the next refresh, and would do it again.
 */
export function useWorkerTasks() {
  const query = useQuery({
    queryKey: ['me-tasks'],
    queryFn: () => api<{ today: string; from: string; to: string; gpsEnabled?: boolean; shareLocation?: boolean; tasks: WorkerTask[] }>('/api/me/tasks'),
    refetchInterval: 5 * 60_000,
  })

  const [pending, setPending] = useState<Set<string>>(new Set())
  useEffect(() => {
    const refresh = () => void pendingTaskIds().then(setPending)
    refresh()
    return subscribeOutbox(refresh)
  }, [])

  const today = query.data?.today ?? todayIso()

  const tasks = useMemo<OverlayTask[]>(
    () =>
      (query.data?.tasks ?? []).map((task) =>
        pending.has(task.id)
          ? { ...task, status: 'done' as const, pendingUpload: true }
          : { ...task, pendingUpload: false },
      ),
    [query.data, pending],
  )

  const openToday = tasks.filter((task) => task.dueDate <= today && task.status !== 'done').length
  const openUpcoming = tasks.filter((task) => task.dueDate > today && task.status !== 'done').length

  /** Tasks grouped by day, ascending, the shape both the plan and the strip need. */
  const byDay = useMemo(() => {
    const map = new Map<string, OverlayTask[]>()
    for (const task of tasks) {
      const list = map.get(task.dueDate)
      if (list) list.push(task)
      else map.set(task.dueDate, [task])
    }
    return map
  }, [tasks])

  return { ...query, today, tasks, byDay, openToday, openUpcoming, to: query.data?.to ?? today }
}
